import React, { useMemo } from 'react';
import { PauseCircleIcon } from '@heroicons/react/24/outline';
import styles from './index.module.scss';
import { Message } from '../chatBox';
import { message, Spin } from 'antd';
import Chart from './chart';
import ThinkingBlock from './ThinkingBlock';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import {
  CopyOutlined,
  FileExcelOutlined,
  FileWordOutlined,
  FilePdfOutlined,
  FileOutlined,
  PictureOutlined,
  PauseCircleOutlined,
} from '@ant-design/icons';
import BaseTable from './table';
import { Role } from '@/pages/chat/type';
import type { MessagePart } from '../chatBox/util';
import { useThemeImage } from '@/hooks/useThemeImage';
import type { ThinkingTimelineRound } from '../chatBox/sseEvent';
import { useTypewriterText } from './useTypewriterText';

export interface MessageCardProps {
  loading?: boolean;
  msg?: Message;
  handleStop?: () => void;
  isThinking?: boolean;
  hideReplyAvatar?: boolean;
  onTyping?: () => void;
  onRenderComplete?: (content: string) => void;
  children?: React.ReactNode;
  copyText?: string;
  fullWidth?: boolean;
  hideOperations?: boolean;
  showThinking?: boolean;
  enableTypewriter?: boolean;
  forceCompleteTypewriter?: boolean;
}

// 文件类型配置
const FILE_CONFIG: Record<string, { icon: React.ReactNode; label: string }> = {
  excel: { icon: <FileExcelOutlined className="text-green-600 text-xl" />, label: 'Excel文件' },
  word: { icon: <FileWordOutlined className="text-blue-600 text-xl" />, label: 'Word文件' },
  pdf: { icon: <FilePdfOutlined className="text-red-600 text-xl" />, label: 'PDF文件' },
  file: { icon: <FileOutlined className="text-gray-600 text-xl" />, label: '文件' },
};

/** 渲染文件类型消息 */
const renderFilePart = (type: string, part: MessagePart, index: number) => {
  const config = FILE_CONFIG[type] || FILE_CONFIG.file;
  const { fileName, fileKey } = part.data || {};
  const name = fileName || config.label;

  return (
    <div key={index} className={`${styles.fileCard} flex items-center gap-2 p-3 rounded-lg`}>
      {config.icon}
      <span className="text-sm">{name}</span>
      {fileKey && (
        <a
          href={fileKey}
          download={name}
          className="text-xs text-blue-500 hover:text-blue-600 ml-auto"
          target="_blank"
          rel="noopener noreferrer"
        >
          下载
        </a>
      )}
    </div>
  );
};

/** 渲染图片类型消息 */
const renderImagePart = (part: MessagePart, index: number) => {
  const { url, fileKey, alt = '图片' } = part.data || {};
  const imageUrl = url || fileKey || '';

  return (
    <div key={index} className={`${styles.imageCard} my-2`}>
      {imageUrl ? (
        <img src={imageUrl} alt={alt} className="max-w-full rounded-lg" style={{ maxHeight: '300px' }} />
      ) : (
        <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
          <PictureOutlined className="text-blue-500 text-xl" />
          <span className="text-sm text-gray-500">图片加载中...</span>
        </div>
      )}
    </div>
  );
};

/** 渲染工具状态消息 */
const renderToolStatusPart = (part: MessagePart, index: number) => (
  <div key={index} className={`my-2 inline-block rounded-xl px-2 py-1 text-xs ${styles.toolInfo}`}>
    {part.data}
  </div>
);

const TYPEWRITER_PART_TYPES = new Set<MessagePart['type']>([
  'text',
  'markdown',
  'thinkingBlock',
  'thinkingTimeline',
]);
const THINKING_PART_TYPES = new Set<MessagePart['type']>(['thinkingBlock', 'thinkingTimeline', 'toolStatus']);

const isTypewriterPart = (part: MessagePart) => TYPEWRITER_PART_TYPES.has(part.type);

const TypewriterMarkdown = React.memo(
  ({
    content,
    active,
    onTyping,
    onComplete,
    forceComplete,
    showInlineLoading,
  }: {
    content?: string;
    active?: boolean;
    onTyping?: () => void;
    onComplete?: (content: string) => void;
    forceComplete?: boolean;
    showInlineLoading?: boolean;
  }) => {
    const displayText = useTypewriterText(String(content ?? ''), {
      active,
      minStep: 1,
      maxStep: 8,
      onTyping,
      onComplete,
      forceComplete,
    });

    return (
      <div className={`${styles.markdownContent} ${showInlineLoading ? styles.markdownContentLoading : ''}`}>
        <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
          {displayText}
        </ReactMarkdown>
        {showInlineLoading && (
          <span className={styles.inlineLoading}>
            <Spin spinning size="small" />
          </span>
        )}
      </div>
    );
  },
);

const MessageCard = ({
  msg,
  loading,
  handleStop,
  hideReplyAvatar,
  onTyping,
  onRenderComplete,
  children,
  copyText,
  fullWidth = false,
  hideOperations = false,
  showThinking = true,
  enableTypewriter = false,
  forceCompleteTypewriter = false,
}: MessageCardProps) => {
  const { role, parts, replying } = msg || {};
  const isAssistant = role === Role.ASSISTANT;
  const visibleParts = useMemo(
    () => (showThinking ? parts : parts?.filter(part => !THINKING_PART_TYPES.has(part.type))),
    [parts, showThinking],
  );
  const hasThinkingPart = Boolean(parts?.some(part => THINKING_PART_TYPES.has(part.type)));
  const isWideAssistantMessage = isAssistant && !fullWidth && (!showThinking || !hasThinkingPart);

  // 根据主题获取图片
  const avatar = useThemeImage('avatar');

  const onCopy = async () => {
    try {
      const textContent = copyText ?? visibleParts!
        .filter(p => p.type === 'text' || p.type === 'error' || p.type === 'markdown')
        .map(p => p.data)
        .join('\n');

      if (!textContent.trim()) {
        message.warning('没有可复制的文本内容');
        return;
      }
      await navigator.clipboard.writeText(textContent);
      message.success('复制成功');
    } catch {
      message.error('复制失败');
    }
  };

  // 缓存渲染结果，只在相关 props 变化时重新计算
  const messageContent = useMemo(() => {
    if (!visibleParts) return null;

    // 正在思考中（初始状态）
    if (!children && loading && replying && visibleParts.length === 0) {
      if (!showThinking) {
        return (
          <div className={`${styles.messageBox} ${styles.thinkingBox} flex items-center`}>
            <Spin spinning size="small" />
          </div>
        );
      }
      return (
        <div className={`${styles.messageBox} ${styles.thinkingBox} flex items-center`}>
          能源助手正在思考中
          <Spin spinning size="small" className="ml-2" />
        </div>
      );
    }

    // 停止生成
    if (!children && !loading && !replying && visibleParts.length === 0) {
      return <div className={`${styles.messageBox} ${styles.stoppedBox} flex items-center`}>停止生成</div>;
    }

    const activeTypewriterPartIndex =
      enableTypewriter && isAssistant
        ? visibleParts.reduce((activeIndex, part, index) => {
            if (isTypewriterPart(part) && part.replying === true) {
              return index;
            }
            return activeIndex;
          }, -1)
        : -1;
    const lastTextPartIndex = visibleParts.reduce(
      (lastIndex, part, index) => (part.type === 'text' || part.type === 'markdown' ? index : lastIndex),
      -1,
    );
    const shouldShowReplyLoading = !loading && !!replying;

    return (
      <div className={`${styles.messageBox} ${isAssistant ? styles.assistantMessage : styles.userMessage}`}>
        {visibleParts.map((part, index) => {
          const { type, data } = part;
          const partShouldType = activeTypewriterPartIndex === index;
          const partShouldComplete =
            forceCompleteTypewriter || (enableTypewriter && isAssistant && isTypewriterPart(part) && !partShouldType);

          if (type === 'thinkingBlock') {
            return (
              <ThinkingBlock
                key={index}
                content={data}
                replying={partShouldType}
                thinkingTime={part.thinkingTime}
                onTyping={onTyping}
                enableTypewriter={partShouldType}
                forceCompleteTypewriter={partShouldComplete}
              />
            );
          }
          if (type === 'thinkingTimeline') {
            return (
              <ThinkingBlock
                key={index}
                rounds={data as ThinkingTimelineRound[]}
                replying={partShouldType}
                onTyping={onTyping}
                enableTypewriter={partShouldType}
                forceCompleteTypewriter={partShouldComplete}
              />
            );
          }
          if (type === 'text' || type === 'markdown') {
            return (
              <TypewriterMarkdown
                key={index}
                content={data}
                active={partShouldType}
                onTyping={onTyping}
                onComplete={index === lastTextPartIndex ? onRenderComplete : undefined}
                forceComplete={partShouldComplete}
                showInlineLoading={shouldShowReplyLoading && index === lastTextPartIndex}
              />
            );
          }
          if (type === 'toolStatus') return renderToolStatusPart(part, index);
          if (type === 'chart' && data) return <Chart key={index} option={data} />;
          if (type === 'table' && data) return <BaseTable key={index} data={data} />;
          if (type in FILE_CONFIG) return renderFilePart(type, part, index);
          if (type === 'image') return renderImagePart(part, index);
          if (type === 'error')
            return (
              <p key={index} className={styles.textDanger}>
                {data}
              </p>
            );

          return null;
        })}
        {children}
        {shouldShowReplyLoading && lastTextPartIndex === -1 && <Spin spinning size="small" className="ml-2" />}
      </div>
    );
  }, [
    visibleParts,
    loading,
    replying,
    isAssistant,
    onTyping,
    onRenderComplete,
    children,
    showThinking,
    enableTypewriter,
    forceCompleteTypewriter,
  ]);

  return (
    <div className={`flex  ${isAssistant ? 'justify-start' : 'justify-end'} mb-4`}>
      {isAssistant && replying && !hideReplyAvatar && (
        <div className={styles.avatarContainer}>
          <img src={avatar} alt="" width={24} className={`absolute top-2 left-2 ${styles.spinning}`} />
        </div>
      )}
      <div
        className={`${fullWidth ? styles.fullWidthMessageContent : styles.messageContent} ${
          isWideAssistantMessage ? styles.wideMessageContent : ''
        }`}
      >
        {messageContent}
        {/* 底部工具栏 */}
        {isAssistant && !replying && !hideOperations && (
          <div onClick={onCopy} className={styles.operationIcon}>
            <CopyOutlined />
          </div>
        )}
        {isAssistant && replying && !hideOperations && (
          <div onClick={handleStop} className={styles.operationIcon}>
            <PauseCircleOutlined />
            停止生成
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(MessageCard);
